import React, { useState } from "react";
import dayjs from "dayjs";
import { useCreate } from "@pankod/refine-core";
import {
  Input,
  notificationProvider,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  useTable,
} from "@pankod/refine-antd";

import { AmcCollectionAccountRow } from "../types";

const { Text } = Typography;
const PAGE_SIZE = 20;

/**
 * Every imported AMC collection account (listed vendor) and whether users can see it, via
 * `pvtGetAmcCollectionAccountsForKodoPay`. AMC accounts must always be hidden from users, so a
 * "Visible" row is flagged as an issue — hide it with the visibility action (unhiding is blocked).
 *
 * Paging is driven through metaData (current/limit), as the konsole getList reads it from there; a new
 * search resets to page 1. `refetchKey` changes when a visibility update succeeds, forcing a reload.
 *
 * The "Virtual account" toggle marks an account (e.g. an MFU escrow account) as virtual via
 * `pvtToggleVirtualAccountForListedVendor`; payouts then never pays it via IMPS (NEFT up to ₹2L, RTGS above).
 */
export const AmcCollectionAccountsTable: React.FC<{ refetchKey: number }> = ({ refetchKey }) => {
  const [current, setCurrent] = useState(1);
  const [keyword, setKeyword] = useState<string | undefined>(undefined);

  const { mutate, isLoading: isUpdating } = useCreate();

  const { tableProps, tableQueryResult } = useTable<AmcCollectionAccountRow>({
    dataProviderName: "grapghQl",
    resource: "pvtGetAmcCollectionAccountsForKodoPay",
    syncWithLocation: false,
    metaData: {
      type: "query",
      current,
      limit: PAGE_SIZE,
      keyword,
      refetchKey,
    },
  });

  const setVirtualAccount = (row: AmcCollectionAccountRow, isVirtualAccount: boolean) => {
    mutate(
      {
        resource: "pvtToggleVirtualAccountForListedVendor",
        dataProviderName: "grapghQl",
        values: { input: { vendorId: row.vendorId, isVirtualAccount } },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          notificationProvider.open({
            message: isVirtualAccount ? "Marked as a virtual account" : "Unmarked as a virtual account",
            type: "success",
            description: row.vendorName,
          });
          tableQueryResult.refetch();
        },
        onError: (err: any) => {
          notificationProvider.open({
            message: err?.message || "Update failed",
            type: "error",
            description: "",
          });
        },
      },
    );
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Input.Search
        allowClear
        placeholder="Search by vendor name, account number or IFSC"
        style={{ maxWidth: 420 }}
        onSearch={(value) => {
          setKeyword(value.trim() || undefined);
          setCurrent(1);
        }}
      />
      <Table
        {...tableProps}
        rowKey="vendorId"
        pagination={{
          ...tableProps.pagination,
          current,
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
          onChange: (page) => setCurrent(page),
        }}
        scroll={{ x: true }}
      >
        <Table.Column<AmcCollectionAccountRow> dataIndex="vendorName" title="Vendor name" />
        <Table.Column<AmcCollectionAccountRow>
          dataIndex="accountNumber"
          title="Account number"
          render={(value: string) => <Text copyable>{value}</Text>}
        />
        <Table.Column<AmcCollectionAccountRow> dataIndex="ifsc" title="IFSC" />
        <Table.Column<AmcCollectionAccountRow>
          dataIndex="accountHolderName"
          title="Account holder"
          render={(value?: string | null) => value || "-"}
        />
        <Table.Column<AmcCollectionAccountRow>
          dataIndex="isHiddenFromUser"
          title="Visible to users"
          render={(hidden: boolean) =>
            hidden ? <Tag color="green">Hidden</Tag> : <Tag color="red">Visible — should be hidden</Tag>
          }
        />
        <Table.Column<AmcCollectionAccountRow>
          dataIndex="isVirtualAccount"
          title="Virtual account"
          render={(isVirtual: boolean, row: AmcCollectionAccountRow) => (
            <Popconfirm
              title={
                isVirtual
                  ? "Unmark as virtual? Payments up to ₹5L will go via IMPS again."
                  : "Mark as virtual? Payments will go via NEFT up to ₹2L and RTGS above, never IMPS."
              }
              okText="Yes"
              cancelText="No"
              onConfirm={() => setVirtualAccount(row, !isVirtual)}
            >
              <Switch checked={isVirtual} loading={isUpdating} checkedChildren="Virtual" unCheckedChildren="No" />
            </Popconfirm>
          )}
        />
        <Table.Column<AmcCollectionAccountRow>
          dataIndex="createdAt"
          title="Imported on"
          render={(value: string) => (value ? dayjs(value).format("DD MMM YYYY") : "-")}
        />
        <Table.Column<AmcCollectionAccountRow>
          dataIndex="vendorId"
          title="Vendor id"
          render={(value: string) => <Text copyable={{ text: value }}>{value.slice(0, 8)}…</Text>}
        />
      </Table>
    </Space>
  );
};
